import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ArrowLeft, CheckCheck, Coffee, Inbox, Layers, ListChecks, LogOut, PauseCircle, Undo2, WifiOff, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BREAK_PROMPT_EVERY,
  SORT_ORDERS,
  SORT_ORDER_LABELS,
  type SortingSessionView,
  type SortingStartResponse,
  type SortingSummary,
  type SortOrder,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Kbd, MOD_KEY } from '../../../components/Kbd';
import { Modal } from '../../../components/Modal';
import { Popover, usePopover } from '../../../components/Popover';
import { Select } from '../../../components/Select';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { toneCardClass } from '../../../components/Tone';
import { STAGE_TONES } from '../../../components/status-tones';
import { ForbiddenState, NotFoundState, ServerErrorState, SystemState } from '../../../components/system-states/SystemState';
import { createUserPreferenceStore } from '../../../lib/preferences';
import { useNow } from '../../../lib/use-now';
import { useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useSortingStart } from '../api/sorting-api';
import { prefetchRankingDetail, useRankingDetail } from '../../ranking/api/ranking-api';
import { CvPane, evidenceWords } from '../../ranking/components/WhyThisScore';
import { CvViewer } from '../../../components/cv/CvViewer';
import { prefetchPdf } from '../../../components/cv/pdf-cache';
import { DecisionPanel } from '../components/DecisionPanel';
import { ReasonPicker, type PickerMode } from '../components/ReasonPicker';
import { useSortingSession, type SortingSession } from '../hooks/use-sorting-session';
import { reviewCardExitHref } from '../review-card-origin';
import styles from './SortingPage.module.css';
import { useModule } from '../../../lib/modules';
import { useCvBankAccess } from '../../cv-bank/hooks/use-cv-bank-actions';

/** Sorting rejects or pools; eliminating belongs to the comparison (S22). */
type SortingPicker = Extract<PickerMode, 'reject' | 'pool'>;

const HINTS_KEY = 'sorting.hints.dismissed';
const MINUTE_MS = 60_000;

/**
 * SCR-01 — sorting mode, which is HR's review card (R6, §L31). One application fills the
 * screen; one key decides it, and the next is already there. A shortlists, H is Maybe (back
 * round once To review is empty), R rejects with a reason, Ctrl+Z undoes. Build spec:
 * 05-screen-inventory Part 5, the S15 planning decisions (DECISIONS-LOG S15 Q1–Q7), and
 * DECISIONS-LOG "Start R6".
 */
export function SortingPage({ jobId }: { readonly jobId: string }): ReactNode {
  // S16 "Sort selected": the applications ticked on SCR-02, handed over in history state.
  const only = useRouterState({ select: (state) => state.location.state.sortOnly });
  const from = useRouterState({ select: (state) => state.location.state.reviewFrom });
  const start = useSortingStart(jobId, only);

  if (start.isPending) return <SortingSkeleton />;
  if (start.error) {
    if (start.error.status === 403) return <ForbiddenState resource="sorting on this job" contact="Your admin" />;
    if (start.error.status === 404) return <NotFoundState />;
    if (start.error.status === 409) return <Ended jobId={jobId} message={start.error.message} />;
    return <ServerErrorState onRetry={() => void start.refetch()} />;
  }
  const { session } = start.data;
  if (!session) {
    return (
      <div className={styles.centred}>
        <SystemState
          icon={Inbox}
          title="Nobody waiting for review"
          body={`Everyone on ${start.data.job.title} has been reviewed. New candidates join the queue as they arrive.`}
          actions={
            <Link to="/jobs/$jobId" params={{ jobId }} className={styles.linkButton}>
              Back to the job
            </Link>
          }
        />
      </div>
    );
  }
  return <SortingScreen start={start.data} session={session} exitHref={reviewCardExitHref(jobId, from)} />;
}

interface SortingScreenProps {
  readonly start: SortingStartResponse;
  readonly session: SortingSessionView;
  /** Group 3: the Ranking or Review page the card was opened from, else the job. */
  readonly exitHref: string;
}

function SortingScreen({ start, session: initial, exitHref }: SortingScreenProps): ReactNode {
  const sorting = useSortingSession(start, initial);
  const queryClient = useQueryClient();
  // R1 (§L34): no Pool button or P key while Talent pools is switched off.
  const canPool = useModule('pools') && start.can.pool;
  // Group 3 Q2: the board's "Also add to CV Bank", for someone who may add to it (the module's on inside the picker).
  const mayAddToCvBank = useCvBankAccess().canEdit;
  const navigate = useNavigate();
  const toast = useToasts((state) => state.show);
  const reject = usePopover();
  const pool = usePopover();
  const [picker, setPicker] = useState<SortingPicker | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [breakSeenAt, setBreakSeenAt] = useState(0);
  const noteRef = useRef<HTMLTextAreaElement | null>(null);
  const job = start.job;
  const { current } = sorting;
  // RNK-02's evidence, marked in the CV on the left (§L30, R6).
  const detail = useRankingDetail(current?.applicationId ?? null);
  const shownDetail = detail.data?.applicationId === current?.applicationId ? detail.data : undefined;
  const highlights = useMemo(() => (shownDetail ? evidenceWords(shownDetail) : []), [shownDetail]);

  const decided = sorting.counters.shortlisted + sorting.counters.rejected + sorting.counters.pooled;
  const milestone = Math.floor(decided / BREAK_PROMPT_EVERY) * BREAK_PROMPT_EVERY;
  const isBreakDue = milestone > 0 && milestone > breakSeenAt;

  // F8.02: the next three CVs are parsed, and their breakdowns fetched, while this one is read.
  useEffect(() => {
    for (const card of sorting.upcoming) {
      if (card.cv?.kind === 'pdf' && !card.cv.isRemoved) prefetchPdf(card.cv.documentId);
      prefetchRankingDetail(queryClient, card.applicationId);
    }
  }, [sorting.upcoming, queryClient]);

  // Leaving by any other route (a link in the rail) still sends what is waiting and
  // ends the session. A tick late, and cancelled by a remount: React's development
  // mode mounts every screen twice, and that must not end the sitting it just began.
  const finishRef = useRef(sorting.finish);
  const leaveTimer = useRef<number | null>(null);
  useEffect(() => {
    finishRef.current = sorting.finish;
  });
  useEffect(() => {
    if (leaveTimer.current !== null) window.clearTimeout(leaveTimer.current);
    return () => {
      leaveTimer.current = window.setTimeout(() => void finishRef.current(), 0);
    };
  }, []);

  function openPicker(mode: SortingPicker): void {
    if (!current) return;
    setPicker(mode);
    (mode === 'reject' ? reject : pool).open();
  }

  function closePicker(): void {
    (picker === 'pool' ? pool : reject).close();
    setPicker(null);
  }

  function leave(): void {
    sorting.saveNote();
    if (sorting.isOffline && sorting.pendingCount > 0) {
      setIsLeaving(true);
      return;
    }
    goBack();
  }

  function goBack(): void {
    void navigate({ href: exitHref });
    void sorting.finish().then((summary) => {
      if (summary) toast({ tone: 'info', message: 'Review session ended', detail: summaryLine(summary) });
    });
  }

  // SCR-01 §10 as §L31 keeps them: A shortlist, H maybe, R reject, P pool, N notes, Ctrl+Z, Esc.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || picker !== null || isLeaving) return;
      if (isBreakDue) setBreakSeenAt(milestone);
      const target = event.target;
      const isTyping = target instanceof HTMLElement && (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
      if (event.key === 'Escape') {
        event.preventDefault();
        if (isTyping && target instanceof HTMLElement) target.blur();
        else leave();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !isTyping) {
        event.preventDefault();
        sorting.undo();
        return;
      }
      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === 'a') sorting.decide('shortlist');
      else if (key === 'r') openPicker('reject');
      else if (key === 'p' && canPool) openPicker('pool');
      else if (key === 'h') sorting.decide('maybe');
      else if (key === 'n') noteRef.current?.focus();
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const sortOptions = useMemo(() => SORT_ORDERS.map((order) => ({ value: order, label: SORT_ORDER_LABELS[order] })), []);

  return (
    <div className={styles.screen}>
      <header className={styles.topBar}>
        <div className={styles.jobBlock}>
          <a
            href={exitHref}
            className={styles.back}
            onClick={(event) => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              leave();
            }}
          >
            <ArrowLeft size={14} aria-hidden />
            {job.reference}
          </a>
          <h1 className={styles.title}>{job.title}</h1>
        </div>
        {sorting.status === 'active' && (
          <p className={styles.progress} aria-live="polite">
            <strong>{sorting.position}</strong> of {sorting.total}
          </p>
        )}
        <Counters session={sorting} />
        <div className={styles.topActions}>
          {sorting.pendingCount > 0 && sorting.isOffline && (
            <Chip tone="warning">
              <WifiOff size={12} aria-hidden /> {sorting.pendingCount} waiting to sync
            </Chip>
          )}
          <div className={styles.sort}>
            <Select label="Order" options={sortOptions} value={sorting.sortOrder} onChange={(value) => sorting.changeSort(sortOrderOf(value))} />
          </div>
          <Button variant="ghost" icon={LogOut} onClick={leave}>
            Exit <Kbd>Esc</Kbd>
          </Button>
        </div>
      </header>

      <HintBar />
      {isBreakDue && sorting.status === 'active' && (
        <p className={styles.breakPrompt} role="status">
          <Coffee size={16} aria-hidden />
          {milestone} decisions — a short break keeps judgement sharp.
        </p>
      )}

      {sorting.status === 'ended' && <Ended jobId={job.id} message={sorting.endedMessage ?? ''} />}
      {sorting.status === 'exhausted' && <Completed session={sorting} onBack={goBack} />}
      {sorting.status === 'loading' && <RegionsSkeleton />}
      {sorting.status === 'active' && current && (
        <>
          <div className={styles.regions}>
            <section className={styles.cv} aria-label="CV">
              {/* A Word CV shows as the text the ranking read, its words marked (RNK-02's pane). */}
              {shownDetail?.cv.kind === 'word' && shownDetail.cv.text ? <CvPane detail={shownDetail} highlights={highlights} /> : <CvViewer cv={current.cv} candidateName={current.candidate.fullName} highlights={highlights} />}
            </section>
            <section className={`${styles.panel} ${toneCardClass()}`} data-tone={STAGE_TONES[current.stage]} aria-label="Application">
              <DecisionPanel ref={noteRef} card={current} jobId={job.id} reviewLines={start.reviewLines} note={sorting.note} onNoteChange={sorting.setNote} onNoteBlur={sorting.saveNote} />
            </section>
          </div>

          <footer className={styles.bottomBar} aria-label="Decisions">
            <Button variant="solid" icon={ListChecks} onClick={() => sorting.decide('shortlist')}>
              Shortlist <Kbd inverse>A</Kbd>
            </Button>
            <Button variant="quiet" icon={PauseCircle} onClick={() => sorting.decide('maybe')}>
              Maybe <Kbd>H</Kbd>
            </Button>
            <Button ref={reject.triggerRef} variant="danger" icon={X} onClick={() => openPicker('reject')}>
              Reject <Kbd>R</Kbd>
            </Button>
            {canPool && (
              <Button ref={pool.triggerRef} variant="outline" icon={Layers} onClick={() => openPicker('pool')}>
                Pool <Kbd>P</Kbd>
              </Button>
            )}
            <span className={styles.spacer} />
            <Button variant="ghost" icon={Undo2} onClick={sorting.undo} disabled={!sorting.canUndo}>
              Undo <Kbd>{MOD_KEY}</Kbd>
              <Kbd>Z</Kbd>
            </Button>
          </footer>

          <Popover anchor={picker === 'reject' ? reject.anchor : picker === 'pool' ? pool.anchor : null} onClose={closePicker} label={picker === 'pool' ? 'Pool' : 'Reject'} align="left">
            {picker !== null && (
              <ReasonPicker
                key={`${picker}-${current.applicationId}`}
                mode={picker}
                reasons={start.reasons}
                pools={sorting.pools}
                canPool={canPool}
                contactAttempts={current.contactAttempts}
                candidateName={current.candidate.fullName}
                canAddToCvBank={mayAddToCvBank && current.cv !== null && !current.cv.isRemoved}
                onConfirm={(choice) => {
                  closePicker();
                  sorting.decide(picker, choice);
                }}
              />
            )}
          </Popover>
        </>
      )}

      <Modal
        isOpen={isLeaving}
        onClose={() => setIsLeaving(false)}
        title="Leave with decisions still unsent?"
        description={`${sorting.pendingCount} ${sorting.pendingCount === 1 ? 'decision has' : 'decisions have'} not reached the server yet because the connection is down. Stay and they will send when it comes back.`}
        footer={
          <>
            <Button variant="quiet" onClick={() => setIsLeaving(false)}>
              Stay
            </Button>
            <Button variant="dangerSolid" onClick={goBack}>
              Leave anyway
            </Button>
          </>
        }
      >
        <p className={styles.modalBody}>Leaving now may lose them.</p>
      </Modal>
    </div>
  );
}

function Counters({ session }: { readonly session: SortingSession }): ReactNode {
  const { counters } = session;
  return (
    <ul className={styles.counters} aria-label="This session">
      <li>
        <strong>{counters.shortlisted}</strong> shortlisted
      </li>
      <li>
        <strong>{counters.maybe}</strong> maybe
      </li>
      <li>
        <strong>{counters.rejected}</strong> rejected
      </li>
      {counters.pooled > 0 && (
        <li>
          <strong>{counters.pooled}</strong> pooled
        </li>
      )}
    </ul>
  );
}

/** 06-qol §2: a compact hint bar on screens with their own keys, dismissible and remembered. */
function HintBar(): ReactNode {
  const userId = useCurrentSession()?.id ?? 'anonymous';
  const store = useMemo(() => createUserPreferenceStore(userId), [userId]);
  const [isDismissed, setIsDismissed] = useState(() => store.get(HINTS_KEY, false));
  const isPoolsOn = useModule('pools');
  if (isDismissed) return null;
  return (
    <div className={styles.hints} role="note" aria-label="Keyboard shortcuts">
      <span>
        <Kbd>A</Kbd> shortlist
      </span>
      <span>
        <Kbd>H</Kbd> maybe
      </span>
      <span>
        <Kbd>R</Kbd> reject
      </span>
      {isPoolsOn && (
        <span>
          <Kbd>P</Kbd> pool
        </span>
      )}
      <span>
        <Kbd>N</Kbd> notes
      </span>
      <span>
        <Kbd>{MOD_KEY}</Kbd>
        <Kbd>Z</Kbd> undo
      </span>
      <span>
        <Kbd>↑</Kbd>
        <Kbd>↓</Kbd> scroll the CV
      </span>
      <span>
        <Kbd>Esc</Kbd> exit
      </span>
      <Button
        size="sm"
        variant="ghost"
        icon={X}
        aria-label="Hide these hints"
        onClick={() => {
          store.set(HINTS_KEY, true);
          setIsDismissed(true);
        }}
      />
    </div>
  );
}

/** SCR-01 state f: the queue is done. */
function Completed({ session, onBack }: { readonly session: SortingSession; readonly onBack: () => void }): ReactNode {
  const now = useNow();
  const minutes = Math.max(1, Math.round((now - new Date(session.startedAt).getTime()) / MINUTE_MS));
  const { counters } = session;
  return (
    <div className={styles.centred}>
      <SystemState
        icon={CheckCheck}
        title="Review done for now"
        body={`${counters.shortlisted} shortlisted, ${counters.maybe} maybe, ${counters.rejected + counters.pooled} rejected in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}. Anyone still in Maybe comes back next time.`}
        actions={
          <div className={styles.completedActions}>
            <Button variant="solid" onClick={onBack}>
              Back to the job
            </Button>
            {session.canUndo && (
              <Button variant="ghost" icon={Undo2} onClick={session.undo}>
                Undo the last decision
              </Button>
            )}
          </div>
        }
      />
    </div>
  );
}

/** SCR-01 state i: the job closed (or the session ended) while sorting. */
function Ended({ jobId, message }: { readonly jobId: string; readonly message: string }): ReactNode {
  return (
    <div className={styles.centred}>
      <SystemState
        icon={Inbox}
        title="Review has ended"
        body={message}
        actions={
          <Link to="/jobs/$jobId" params={{ jobId }} className={styles.linkButton}>
            Back to the job
          </Link>
        }
      />
    </div>
  );
}

function SortingSkeleton(): ReactNode {
  return (
    <div className={styles.screen}>
      <div className={styles.topBar}>
        <Skeleton shape="title" width="30%" />
      </div>
      <RegionsSkeleton />
    </div>
  );
}

/** SCR-01 state b: shaped like the card that is coming, never a spinner. */
function RegionsSkeleton(): ReactNode {
  return (
    <div className={styles.regions}>
      <section className={styles.cv}>
        <Skeleton shape="card" />
      </section>
      <section className={`${styles.panel} ${toneCardClass()}`} data-tone={STAGE_TONES.new}>
        <div className={styles.skeletonPanel}>
          <Skeleton shape="title" width="60%" />
          <SkeletonLines count={8} />
        </div>
      </section>
    </div>
  );
}

function summaryLine(summary: SortingSummary): string {
  const minutes = Math.max(1, Math.round(summary.durationMs / MINUTE_MS));
  const { counters } = summary;
  return `${counters.shortlisted} shortlisted · ${counters.maybe} maybe · ${counters.rejected + counters.pooled} rejected, in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
}

function sortOrderOf(value: string): SortOrder {
  return SORT_ORDERS.find((order) => order === value) ?? 'score_desc';
}
